var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { getIdenticonsVariableOverrides } from '../../identicons';
import { Label } from '../../label';
import { useSidebarContext } from '../sidebar-context';
import { styled } from '../../styled';
import clsx from '../../utils/clsx';
import { getSidebarLinkUtilityClass, sidebarLinkComponentName, sidebarLinkClasses, } from './sidebar-link-classes';
import { useMergedClasses } from '../../styles/classes';
import { useMediaQuery } from '../../use-media-query';
import { useTheme } from '../../theme/use-theme';
var Root = styled('li', { name: sidebarLinkComponentName })(function (_a) {
    var _b;
    var theme = _a.theme, ownerState = _a.ownerState;
    return (_b = {},
        _b["& .".concat(sidebarLinkClasses.title, ",& .").concat(sidebarLinkClasses.availabilityLabel, ",& .").concat(sidebarLinkClasses.decoration, ",& .").concat(sidebarLinkClasses.endIcon)] = __assign({}, (ownerState.collapsed
            ? {
                opacity: 0,
                transition: theme.transitions.create('opacity', {
                    easing: theme.transitions.easing.easeIn,
                    duration: theme.transitions.duration.leavingScreen,
                    delay: 0,
                }),
            }
            : {
                opacity: 1,
                willChange: 'opacity',
                transition: theme.transitions.create('opacity', {
                    easing: theme.transitions.easing.easeIn,
                    duration: theme.transitions.duration.enteringScreen,
                    delay: theme.transitions.duration.enteringScreen / 2,
                }),
            })),
        _b);
});
var getIconStyles = function (theme) { return ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& > svg': {
        width: '1em',
        height: '1em',
        transition: theme.transitions.create(['fill', 'transform'], { duration: theme.transitions.duration.short }),
    },
}); };
var StartIcon = styled('span', { name: sidebarLinkComponentName, slot: 'StartIcon' })(function (_a) {
    var theme = _a.theme;
    return (__assign(__assign({}, getIconStyles(theme)), { width: theme.typography.pxToRem(20), fontSize: theme.typography.pxToRem(20) }));
});
var EndIcon = styled('span', { name: sidebarLinkComponentName, slot: 'EndIcon' })(function (_a) {
    var theme = _a.theme;
    return (__assign(__assign({}, getIconStyles(theme)), { marginLeft: 'auto', color: theme.tokens.color_fg_decorative }));
});
var Link = styled('a', { name: sidebarLinkComponentName, slot: 'Link' })(function (_a) {
    var _b;
    var theme = _a.theme, ownerState = _a.ownerState;
    return (__assign(__assign(__assign(__assign(__assign(__assign({ fontWeight: theme.typography.fontWeightMedium, padding: theme.spacing(1) }, (ownerState.variant === 'primary' && __assign(__assign({}, theme.typography.body2), { fontWeight: theme.typography.fontWeightMedium, lineHeight: theme.typography.pxToRem(20), color: theme.tokens.color_fg_link }))), (ownerState.variant === 'secondary' && __assign(__assign({}, theme.typography.caption), (_b = { lineHeight: theme.typography.pxToRem(22.4), color: theme.tokens.color_fg_link_subtle, paddingTop: theme.spacing(0.5), paddingBottom: theme.spacing(0.5) }, _b["& .".concat(sidebarLinkClasses.startIcon)] = {
        fontSize: theme.typography.pxToRem(16),
    }, _b)))), { borderRadius: theme.shape.borderRadius, textDecoration: 'none', width: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer', background: 'transparent', border: 'none', transition: theme.transitions.create(['color'], { duration: theme.transitions.duration.shortest }) }), getIdenticonsVariableOverrides('default', theme)), (ownerState.isActive && __assign({ color: theme.tokens.color_fg_selected }, getIdenticonsVariableOverrides('primary', theme)))), { '&:hover': __assign({ color: theme.tokens.color_fg_selected, textDecoration: 'none' }, getIdenticonsVariableOverrides('primary', theme)), '&:focus-visible': __assign({ outline: 'none' }, theme.mixins.focusRing()) }));
});
var Title = styled('span', { name: sidebarLinkComponentName, slot: 'Title' })(function (_a) {
    var theme = _a.theme;
    return ({
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginLeft: theme.spacing(1.25),
        minWidth: theme.typography.pxToRem(32),
    });
});
var AvailabilityLabel = styled(Label, { name: sidebarLinkComponentName, slot: 'AvailabilityLabel' })(function (_a) {
    var theme = _a.theme;
    return ({
        marginLeft: theme.spacing(1),
    });
});
var Decoration = styled('span', { name: sidebarLinkComponentName, slot: 'Decoration' })(function (_a) {
    var theme = _a.theme;
    return ({
        marginLeft: theme.spacing(1),
    });
});
var getFeatureAvailabilityLabelProps = function (featureAvailability) {
    var labelProps = {
        color: 'primary',
        variant: 'default',
    };
    switch (featureAvailability) {
        case 'beta':
            labelProps.children = 'Beta';
            break;
        // First Availability has been replaced by Early Access
        case 'first':
        case 'early':
            labelProps.children = 'Early';
            break;
        case 'new':
            labelProps.variant = 'filled';
            labelProps.children = 'New';
            break;
    }
    return labelProps;
};
export var SidebarLink = React.forwardRef(function (props, ref) {
    var title = props.title, startIcon = props.startIcon, endIcon = props.endIcon, featureAvailability = props.featureAvailability, className = props.className, decoration = props.decoration, titleId = props.titleId, _a = props.isActive, isActive = _a === void 0 ? false : _a, _b = props.variant, variant = _b === void 0 ? 'primary' : _b, _c = props.component, LinkComponent = _c === void 0 ? 'a' : _c, propClasses = props.classes, children = props.children, sx = props.sx, propOnClick = props.onClick, _d = props.shouldCollapseSidebarOnClick, shouldCollapseSidebarOnClick = _d === void 0 ? true : _d, linkProps = __rest(props, ["title", "startIcon", "endIcon", "featureAvailability", "className", "decoration", "titleId", "isActive", "variant", "component", "classes", "children", "sx", "onClick", "shouldCollapseSidebarOnClick"]);
    var theme = useTheme();
    var isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    var sidebarCtx = useSidebarContext();
    var classes = useMergedClasses(sidebarLinkClasses, getSidebarLinkUtilityClass, propClasses);
    var handleClick = function (event) {
        if (propOnClick) {
            propOnClick(event);
        }
        if (!isLargeScreen && !sidebarCtx.collapsed && shouldCollapseSidebarOnClick) {
            sidebarCtx.onCollapseChange();
        }
    };
    return (React.createElement(Root, { ownerState: { collapsed: sidebarCtx.collapsed }, className: clsx(classes.root, className), sx: sx },
        React.createElement(Link, __assign({ ref: ref, as: LinkComponent, title: typeof title === 'string' ? title : undefined, ownerState: { isActive: isActive, variant: variant }, className: classes.link, "aria-current": isActive ? 'page' : undefined, onClick: handleClick }, linkProps),
            React.createElement(StartIcon, { className: clsx(classes.icon, classes.startIcon) }, startIcon),
            React.createElement(Title, { id: titleId, className: sidebarLinkClasses.title }, title),
            !!featureAvailability && (React.createElement(AvailabilityLabel, __assign({ className: classes.availabilityLabel }, getFeatureAvailabilityLabelProps(featureAvailability)))),
            !!decoration && React.createElement(Decoration, { className: classes.decoration }, decoration),
            !!endIcon && React.createElement(EndIcon, { className: clsx(classes.icon, classes.endIcon) }, endIcon)),
        children));
});
